import React, { useEffect, useState } from "react";
import { CardBody, Row, Col, Card, Container, Form, Input, Label, Table, Button, Spinner, Alert } from "reactstrap";
import { useParams, useNavigate } from "react-router-dom";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { getOrder } from "../../api/ecommerce";
import apiClient from "../../api/config";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const InvoiceEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [invoice, setInvoice] = useState(null);
  
  const [items, setItems] = useState([]);
  const [taxTotal, setTaxTotal] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [shippingCost, setShippingCost] = useState(0);

  useEffect(() => {
    getOrder(id)
      .then((data) => {
        setInvoice(data);
        let parsedItems = [];
        try {
          parsedItems = typeof data.items === 'string' ? JSON.parse(data.items) : (data.items || []);
        } catch (e) {}
        setItems(parsedItems.map(item => ({
          ...item,
          price: parseFloat(item.price || item.rate || 0),
          quantity: parseInt(item.quantity || 1, 10),
          total: parseFloat(item.total || 0)
        })));
        setTaxTotal(parseFloat(data.tax_total || 0));
        setDiscount(parseFloat(data.discount || 0));
        setShippingCost(parseFloat(data.shipping_cost || 0));
      })
      .catch((err) => {
        toast.error("Failed to fetch invoice details");
      })
      .finally(() => setLoading(false));
  }, [id]);

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    if (field === "price" || field === "quantity") {
      newItems[index].total = newItems[index].price * newItems[index].quantity;
    }
    setItems(newItems);
  };

  const handleAddItem = () => {
    setItems([...items, { name: "", description: "", price: 0, quantity: 1, total: 0 }]);
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const calculateSubTotal = () => items.reduce((acc, item) => acc + item.total, 0);
  const subTotal = calculateSubTotal();
  const finalTotal = subTotal + parseFloat(taxTotal || 0) + parseFloat(shippingCost || 0) - parseFloat(discount || 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.update(`/api/orders/${id}/invoice`, {
        items,
        subtotal: subTotal,
        tax_total: parseFloat(taxTotal || 0),
        discount: parseFloat(discount || 0),
        shipping_cost: parseFloat(shippingCost || 0),
        total: finalTotal
      });
      toast.success("Invoice updated successfully!");
      setTimeout(() => navigate(`/apps-invoices-details/${id}`), 1500);
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="d-flex justify-content-center mt-5"><Spinner color="primary" /></div>
        </Container>
      </div>
    );
  }

  const isPaid = invoice && invoice.payment_status && invoice.payment_status.toLowerCase() === 'paid';

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Edit Invoice" pageTitle="Invoices" />
        <Row className="justify-content-center">
          <Col xxl={9}>
            {isPaid && (
              <Alert color="danger">
                This invoice is marked as <b>Paid</b> and cannot be edited.
              </Alert>
            )}
            <Card>
              <Form onSubmit={handleSubmit} id="invoice_edit_form">
                <CardBody className="border-bottom border-bottom-dashed p-4">
                  <h4 className="card-title mb-3">Invoice #{invoice?.order_number}</h4>
                  <Row>
                    <Col lg={4}>
                      <Label>Customer Email</Label>
                      <Input type="text" readOnly className="bg-light" value={invoice?.email || ""} />
                    </Col>
                    <Col lg={4}>
                      <Label>Order Status</Label>
                      <Input type="text" readOnly className="bg-light" value={invoice?.order_status || ""} />
                    </Col>
                    <Col lg={4}>
                      <Label>Payment Status</Label>
                      <Input type="text" readOnly className="bg-light" value={invoice?.payment_status || ""} />
                    </Col>
                  </Row>
                </CardBody>

                <CardBody className="p-4">
                  <div className="table-responsive">
                    <Table className="invoice-table table-borderless table-nowrap mb-0">
                      <thead className="align-middle">
                        <tr className="table-active">
                          <th scope="col" style={{ width: "50px" }}>#</th>
                          <th scope="col">Product Details</th>
                          <th scope="col" style={{ width: "120px" }}>Rate</th>
                          <th scope="col" style={{ width: "120px" }}>Quantity</th>
                          <th scope="col" className="text-end" style={{ width: "150px" }}>Amount</th>
                          <th scope="col" className="text-end" style={{ width: "105px" }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, index) => (
                          <tr key={index} className="product">
                            <th scope="row" className="product-id">{index + 1}</th>
                            <td className="text-start">
                              <Input
                                type="text"
                                className="form-control bg-light border-0 mb-2"
                                placeholder="Product Name"
                                value={item.name}
                                onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                                required
                                disabled={isPaid}
                              />
                              <Input
                                type="textarea"
                                className="form-control bg-light border-0"
                                rows="2"
                                placeholder="Product Details"
                                value={item.description || ''}
                                onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                                disabled={isPaid}
                              />
                            </td>
                            <td>
                              <Input
                                type="number"
                                step="0.01"
                                className="form-control bg-light border-0"
                                value={item.price}
                                onChange={(e) => handleItemChange(index, 'price', parseFloat(e.target.value) || 0)}
                                disabled={isPaid}
                              />
                            </td>
                            <td>
                              <div className="input-step">
                                <button type="button" className="minus" onClick={() => !isPaid && handleItemChange(index, 'quantity', Math.max(1, item.quantity - 1))}>–</button>
                                <Input
                                  type="number"
                                  className="product-quantity"
                                  value={item.quantity}
                                  readOnly
                                />
                                <button type="button" className="plus" onClick={() => !isPaid && handleItemChange(index, 'quantity', item.quantity + 1)}>+</button>
                              </div>
                            </td>
                            <td className="text-end">
                              <Input
                                type="text"
                                className="form-control bg-light border-0 product-line-price"
                                value={"£" + item.total.toFixed(2)}
                                readOnly
                              />
                            </td>
                            <td className="product-removal">
                              <Button color="danger" onClick={() => handleRemoveItem(index)} disabled={isPaid}>Delete</Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tbody>
                        <tr>
                          <td colSpan="5">
                            <Button color="soft-secondary" onClick={handleAddItem} disabled={isPaid}>
                              <i className="ri-add-fill me-1 align-bottom"></i> Add Item
                            </Button>
                          </td>
                        </tr>
                        <tr className="border-top border-top-dashed mt-2">
                          <td colSpan="3"></td>
                          <td colSpan="2" className="p-0">
                            <Table className="table-borderless table-sm table-nowrap align-middle mb-0">
                              <tbody>
                                <tr>
                                  <th scope="row">Sub Total</th>
                                  <td style={{ width: "150px" }}>
                                    <Input type="text" className="form-control bg-light border-0" value={"£" + subTotal.toFixed(2)} readOnly />
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row">Estimated Tax</th>
                                  <td>
                                    <Input type="number" step="0.01" className="form-control bg-light border-0" value={taxTotal} onChange={(e) => setTaxTotal(parseFloat(e.target.value) || 0)} disabled={isPaid} />
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row">Discount</th>
                                  <td>
                                    <Input type="number" step="0.01" className="form-control bg-light border-0" value={discount} onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)} disabled={isPaid} />
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row">Shipping Charge</th>
                                  <td>
                                    <Input type="number" step="0.01" className="form-control bg-light border-0" value={shippingCost} onChange={(e) => setShippingCost(parseFloat(e.target.value) || 0)} disabled={isPaid} />
                                  </td>
                                </tr>
                                <tr className="border-top border-top-dashed">
                                  <th scope="row">Total Amount</th>
                                  <td>
                                    <Input type="text" className="form-control bg-light border-0" value={"£" + finalTotal.toFixed(2)} readOnly />
                                  </td>
                                </tr>
                              </tbody>
                            </Table>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                  <div className="mt-4 text-end">
                    <Button color="success" type="submit" disabled={isPaid || saving}>
                      {saving ? <Spinner size="sm" className="me-2" /> : null}
                      Update Invoice
                    </Button>
                  </div>
                </CardBody>
              </Form>
            </Card>
          </Col>
        </Row>
      </Container>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default InvoiceEdit;
